<template>
    <!-- The description goes through the `text` prop, not the default slot: a
         `<template v-if>` still leaves `$slots.default` defined, so KTeaserCard
         would render an empty `__text` div (and its body gap) whenever the tour
         has no description. -->
    <KTeaserCard :text="regions.text ? displayDescription : undefined">
        <template
            v-if="displayImage?.filename"
            #media
        >
            <NuxtLink
                :to="tourUrl"
                @click="select('image')"
            >
                <NuxtImg
                    :src="displayImage.filename"
                    :alt="displayImage.alt || tour.name"
                    width="712"
                    height="400"
                    fit="cover"
                    format="auto"
                    loading="lazy"
                    sizes="xs:100vw sm:100vw md:433px lg:400px"
                />
            </NuxtLink>
        </template>

        <template
            v-if="displayImage?.filename && locationLabel"
            #eyebrow
        >
            {{ locationLabel }}
        </template>

        <template
            v-if="tour.content.duration"
            #meta
        >
            <span>
                <Icon
                    name="tabler:clock"
                    aria-hidden="true"
                />
                {{ tour.content.duration }}
            </span>
        </template>

        <template #title>
            <NuxtLink
                :to="tourUrl"
                @click="select('title')"
            >
                <span
                    v-if="titleHtml"
                    v-html="displayTitle"
                />
                <template v-else>
                    {{ displayTitle }}
                </template>
            </NuxtLink>
        </template>

        <template
            v-if="regions.inclusions && included.length"
            #content
        >
            <KChecklist
                :items="included"
                label="What is included:"
                size="sm"
            >
                <NuxtLink
                    v-if="tour.content.included_more"
                    :to="tourUrl"
                    @click="select('inclusions')"
                >
                    + View {{ tour.content.included_more }} more inclusions &amp; details
                </NuxtLink>
            </KChecklist>
        </template>

        <template
            v-if="regions.price && tour.content.from_price != null"
            #price
        >
            <span>Price per traveler</span>
            <span>from ${{ tour.content.from_price }}</span>
        </template>

        <template
            v-if="regions.action"
            #action
        >
            <KButton
                :as="nuxtLink"
                :to="tourUrl"
                size="sm"
                @click="select('details')"
            >
                Check details
            </KButton>
        </template>
    </KTeaserCard>
</template>

<script setup>
import { KButton, KChecklist, KTeaserCard } from '@vemedia/karibu'
import { computed, resolveComponent } from 'vue'
import resolveDetailLevel from '~/helpers/cardDetailLevels'

const props = defineProps({
    tour: {
        type: Object,
        required: true,
    },
    detail: {
        type: String,
        default: 'full',
    },
    title: {
        type: String,
        default: '',
    },
    titleHtml: {
        type: Boolean,
        default: false,
    },
    description: {
        type: String,
        default: '',
    },
    image: {
        type: Object,
        default: null,
    },
})

const emit = defineEmits(['select'])

const LOCATIONS = {
    safari: 'Safari',
    zanzibar: 'Zanzibar',
    moshi: 'Moshi',
    kilimanjaro: 'Kilimanjaro',
}

const nuxtLink = resolveComponent('NuxtLink')
const regions = computed(() => resolveDetailLevel(props.detail))
const tourUrl = computed(() => `/${props.tour.full_slug}`)
const displayImage = computed(() => props.image?.filename ? props.image : props.tour.content.image)
const displayTitle = computed(() => props.title || props.tour.name)
const displayDescription = computed(() => props.description || props.tour.content.description)
const included = computed(() => props.tour.content.included || [])
const locationLabel = computed(() => LOCATIONS[props.tour.content.location] || '')

function select(action) {
    emit('select', action)
}
</script>
